// buildMonthlyTabsConfig.tsx
import CommentsInput from 'components/Comments/CommentsInput'
import OptimizationsContainer from 'components/Optimizations/OptimizationsContainer'
import OrgDealInfo from 'components/Organizations/OrgDealInfo'
import type { MtbOptimization, OrganizationTableItem, TabItem } from 'types'
import RenderMonthlyTaskList from './RenderMonthlyTaskList'

// Stable references to avoid recreating on every call
const MTB_CONTEXT_FILTERS = ['mtb'] as const
const EMPTY_PROPS = {} as const

type MonthKeyFromISO = (iso: string, tz?: string) => string

type BuildArgs = {
  PK?: string
  organization: any | null
  optimizations?: MtbOptimization
  monthlyTasks?: any[] | null
  seoMonthly?: object
  amMonthly?: object
  dmsMonthly?: object
  assignees?: any[]
  // handlers
  handleMonthlyTaskChange: (...args: any[]) => void
  handleUpdateMonthlyTaskStatus: (...args: any[]) => void
  updateMonthlyTaskActiveAssignee: (...args: any[]) => void

  // comments tab
  combinedSKs?: string[]
  combinedTasksAndOptimizations?: any[]
  handleAddTaskComment: (...args: any[]) => void // kept for parity (not used here)

  // checklists tab
  deals: Partial<OrganizationTableItem>[]
  integrationFields: any
  setIntegrationFields: (v: any) => void
  handleStatusChange: (...args: any[]) => void
  fetchedChecklists?: any[]
  isChecklistsLoading?: boolean
  context: string | 'mtb'

  // layout
  expanded?: boolean

  // optimization accordion state callback
  onExpandedIdChange?: (id: string | null) => void

  // month filtering
  monthKey: string
  monthKeyFromISO: MonthKeyFromISO
  tz?: string
}

/** Component+props tab descriptors (no inline JSX) */
export const buildMonthlyTabsConfig = ({
  organization,
  optimizations,
  monthlyTasks,

  handleMonthlyTaskChange,
  handleUpdateMonthlyTaskStatus,
  updateMonthlyTaskActiveAssignee,

  combinedSKs,
  combinedTasksAndOptimizations,
  context,

  expanded,
  onExpandedIdChange,
  monthKey,
  monthKeyFromISO,
  tz = 'America/Detroit',
}: BuildArgs): TabItem[] => {
  const byMonth = (t?: any) => t && monthKeyFromISO(String(t?.timeCreated), tz) === monthKey
  const list = Array.isArray(monthlyTasks) ? monthlyTasks : []

  const seoMonthly = list.filter(t => t?.isSeoMonthlyTask && byMonth(t))
  const amMonthly = list.filter(t => t?.isAmMonthlyTask && byMonth(t))
  const dmsMonthly = list.filter(t => t?.isDmsMonthlyTask && byMonth(t))

  const assignees = Array.isArray(organization?.team_details) ? organization.team_details : []

  return [
    {
      label: 'main',
      display: 'Optimizations',
      Component: OptimizationsContainer,
      props: {
        organization,
        expanded,
        context: 'mtb',
        monthKey,
        onExpandedIdChange,
      },
    },
    {
      label: 'seo',
      display: 'SEO',
      Component: RenderMonthlyTaskList,
      props: {
        optimizations,
        assignees,
        monthlyTasks: seoMonthly,
        onMonthlyTaskChange: handleMonthlyTaskChange,
        onUpdateStatus: handleUpdateMonthlyTaskStatus,
        onUpdateActiveAssignee: updateMonthlyTaskActiveAssignee,
        expanded,
      },
    },
    {
      label: 'am',
      display: 'AM',
      Component: RenderMonthlyTaskList,
      props: {
        optimizations,
        assignees,
        monthlyTasks: amMonthly,
        onMonthlyTaskChange: handleMonthlyTaskChange,
        onUpdateStatus: handleUpdateMonthlyTaskStatus,
        onUpdateActiveAssignee: updateMonthlyTaskActiveAssignee,
        expanded,
      },
    },
    {
      label: 'dms',
      display: 'DMS',
      Component: RenderMonthlyTaskList,
      props: {
        optimizations,
        assignees,
        monthlyTasks: dmsMonthly,
        onMonthlyTaskChange: handleMonthlyTaskChange,
        onUpdateStatus: handleUpdateMonthlyTaskStatus,
        onUpdateActiveAssignee: updateMonthlyTaskActiveAssignee,
        expanded,
      },
    },
    {
      label: 'comments',
      display: 'Comments',
      Component: CommentsInput,
      props: {
        taskSKs: combinedSKs ?? [],
        contextFilters: MTB_CONTEXT_FILTERS,
        expanded: !!expanded,
        context,
        checklistItems: combinedTasksAndOptimizations ?? [],
      },
    },
    {
      label: 'deals',
      display: 'Deals',
      Component: OrgDealInfo,
      props: EMPTY_PROPS,
    },
  ]
}
